import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { collection, query, orderBy, onSnapshot, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate, Link } from 'react-router-dom';

const WEEKDAYS = ['Да', 'Мя', 'Лх', 'Пү', 'Ба', 'Бя', 'Ня'];
const MONTH_NAMES = [
    '1-р сар', '2-р сар', '3-р сар', '4-р сар', '5-р сар', '6-р сар',
    '7-р сар', '8-р сар', '9-р сар', '10-р сар', '11-р сар', '12-р сар'
];
const DAY_NAMES_FULL = ['Ням', 'Даваа', 'Мягмар', 'Лхагва', 'Пүрэв', 'Баасан', 'Бямба'];

const TASK_COLORS = [
    '#f97316', '#3b82f6', '#10b981', '#f472b6',
    '#a78bfa', '#eab308', '#06b6d4', '#ef4444',
];

function getTaskColor(index) {
    return TASK_COLORS[index % TASK_COLORS.length];
}

function Dashboard({ user, userData }) {
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedImage, setExpandedImage] = useState(null);
    const navigate = useNavigate();

    const today = new Date();
    const [currentMonth, setCurrentMonth] = useState(today.getMonth());
    const [currentYear, setCurrentYear] = useState(today.getFullYear());
    const [selectedDate, setSelectedDate] = useState(today.toISOString().split('T')[0]);
    const [viewMode, setViewMode] = useState('month');
    const [refreshing, setRefreshing] = useState(false);

    // Swipe gesture refs
    const touchStartRef = useRef({ x: 0, y: 0 });
    const touchEndRef = useRef({ x: 0, y: 0 });
    const teamId = userData?.teamId;

    useEffect(() => {
        if (!teamId) return;
        const q = query(collection(db, 'teams', teamId, 'schedules'), orderBy('date', 'asc'));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const scheduleList = [];
            querySnapshot.forEach((docSnap) => {
                scheduleList.push({ id: docSnap.id, ...docSnap.data() });
            });
            scheduleList.sort((a, b) => {
                if (a.date !== b.date) return (a.date || '').localeCompare(b.date || '');
                return (a.time || '').localeCompare(b.time || '');
            });
            setSchedules(scheduleList);
            setLoading(false);
        }, (error) => {
            console.error("Firestore error:", error);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [teamId]);

    const handleDelete = async (id) => {
        if (window.confirm('Энэ хуваарийг устгах уу?')) {
            try {
                await deleteDoc(doc(db, 'teams', teamId, 'schedules', id));
            } catch (err) {
                console.error('Error deleting document', err);
            }
        }
    };

    const schedulesByDate = useMemo(() => {
        const map = {};
        schedules.forEach((schedule, idx) => {
            if (!schedule.date) return;
            schedule._colorIdx = idx;
            if (schedule.endDate && schedule.endDate > schedule.date) {
                const [sy, sm, sd] = schedule.date.split('-').map(Number);
                const [ey, em, ed] = schedule.endDate.split('-').map(Number);
                const start = new Date(sy, sm - 1, sd);
                const end = new Date(ey, em - 1, ed);
                const current = new Date(start);
                while (current <= end) {
                    const key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
                    if (!map[key]) map[key] = [];
                    map[key].push(schedule);
                    current.setDate(current.getDate() + 1);
                }
            } else {
                if (!map[schedule.date]) map[schedule.date] = [];
                map[schedule.date].push(schedule);
            }
        });
        return map;
    }, [schedules]);

    const calendarDays = useMemo(() => {
        const firstDay = new Date(currentYear, currentMonth, 1);
        let startDow = firstDay.getDay() - 1;
        if (startDow < 0) startDow = 6;
        const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
        const prevMonthDays = new Date(currentYear, currentMonth, 0).getDate();
        const days = [];
        for (let i = startDow - 1; i >= 0; i--) {
            const day = prevMonthDays - i;
            const m = currentMonth === 0 ? 11 : currentMonth - 1;
            const y = currentMonth === 0 ? currentYear - 1 : currentYear;
            const dateStr = `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            days.push({ day, dateStr, isOtherMonth: true });
        }
        for (let d = 1; d <= daysInMonth; d++) {
            const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            days.push({ day: d, dateStr, isOtherMonth: false });
        }
        const remaining = 42 - days.length;
        for (let d = 1; d <= remaining; d++) {
            const m = currentMonth === 11 ? 0 : currentMonth + 1;
            const y = currentMonth === 11 ? currentYear + 1 : currentYear;
            const dateStr = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            days.push({ day: d, dateStr, isOtherMonth: true });
        }
        return days;
    }, [currentMonth, currentYear]);

    const goToToday = () => {
        const t = new Date();
        setCurrentMonth(t.getMonth());
        setCurrentYear(t.getFullYear());
        setSelectedDate(t.toISOString().split('T')[0]);
        setViewMode('month');
    };

    const goToPrevMonth = useCallback(() => {
        setCurrentMonth(prev => {
            if (prev === 0) { setCurrentYear(y => y - 1); return 11; }
            return prev - 1;
        });
    }, []);

    const goToNextMonth = useCallback(() => {
        setCurrentMonth(prev => {
            if (prev === 11) { setCurrentYear(y => y + 1); return 0; }
            return prev + 1;
        });
    }, []);
    const handleDayClick = (dateStr) => { setSelectedDate(dateStr); setViewMode('day'); };
    const goBackToMonth = () => { setViewMode('month'); };

    // Pull to refresh
    const handleRefresh = useCallback(() => {
        setRefreshing(true);
        setTimeout(() => setRefreshing(false), 800);
    }, []);

    // Swipe gesture handlers
    const handleTouchStart = useCallback((e) => {
        touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }, []);

    const handleTouchEnd = useCallback((e) => {
        touchEndRef.current = { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
        const dx = touchEndRef.current.x - touchStartRef.current.x;
        const dy = touchEndRef.current.y - touchStartRef.current.y;
        // Only trigger if horizontal swipe is dominant and > 60px
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
            if (dx < 0) {
                // Swipe left → next month
                goToNextMonth();
            } else {
                // Swipe right → prev month
                goToPrevMonth();
            }
        }
        // Pull down to refresh (vertical > 80px, at scroll top)
        if (dy > 80 && Math.abs(dy) > Math.abs(dx) * 2) {
            handleRefresh();
        }
    }, [goToNextMonth, goToPrevMonth, handleRefresh]);

    const todayStr = today.toISOString().split('T')[0];

    const selectedDayTasks = useMemo(() => {
        return selectedDate ? (schedulesByDate[selectedDate] || []) : [];
    }, [selectedDate, schedulesByDate]);

    const formatSelectedDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr + 'T00:00:00');
        const dayName = DAY_NAMES_FULL[d.getDay()];
        const day = d.getDate();
        const year = d.getFullYear();
        return `${dayName} – ${year}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(day).padStart(2, '0')}`;
    };

    const timelineHours = [];
    for (let h = 0; h <= 23; h++) { timelineHours.push(h); }

    const parseTime = (timeStr) => {
        if (!timeStr) return null;
        const parts = timeStr.split(':');
        return parseInt(parts[0], 10) + parseInt(parts[1] || 0, 10) / 60;
    };

    const positionedTasks = useMemo(() => {
        return selectedDayTasks.map(task => {
            const startHour = parseTime(task.time);
            return { ...task, startHour: startHour !== null ? startHour : null };
        });
    }, [selectedDayTasks]);

    const timedTasks = positionedTasks.filter(t => t.startHour !== null);
    const untimedTasks = positionedTasks.filter(t => t.startHour === null);

    return (
        <div className="page-container mobile-page">
            <div className="mobile-page-header">
                <h1 className="mobile-page-title">📅 Хуваарь</h1>
                <Link to={`/create?date=${selectedDate}`} className="cal-add-btn-header">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                </Link>
            </div>

            {loading ? (
                <div className="loading-spinner-inline">
                    <span className="spinner"></span>
                    <p className="mt-2" style={{ color: 'var(--text-muted)' }}>Ачаалж байна...</p>
                </div>
            ) : (
                <>
                    {/* ===== MONTH VIEW ===== */}
                    {viewMode === 'month' && (
                        <div className="cal-month-view"
                            onTouchStart={handleTouchStart}
                            onTouchEnd={handleTouchEnd}
                        >
                            {/* Pull to refresh indicator */}
                            <div className={`pull-refresh-indicator${refreshing ? ' pull-refresh-indicator--refreshing' : ''}`}>
                                {refreshing ? '🔄 Шинэчилж байна...' : '↓ Доош татаж шинэчлэх'}
                            </div>
                            <div className="cal-header">
                                <button className="cal-nav-btn" onClick={goToPrevMonth} aria-label="Өмнөх сар">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                                </button>
                                <div className="cal-title" onClick={goToToday} style={{ cursor: 'pointer' }}>
                                    <span className="cal-title__year">{currentYear}</span>
                                    <span className="cal-title__month">{MONTH_NAMES[currentMonth]}</span>
                                    <span className="cal-title__today-hint">Өнөөдөр ▸</span>
                                </div>
                                <button className="cal-nav-btn" onClick={goToNextMonth} aria-label="Дараах сар">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                                </button>
                            </div>

                            <div className="cal-weekdays">
                                {WEEKDAYS.map(day => (
                                    <div key={day} className="cal-weekday">{day}</div>
                                ))}
                            </div>

                            <div className="cal-grid">
                                {calendarDays.map(({ day, dateStr, isOtherMonth }) => {
                                    const isToday = dateStr === todayStr;
                                    const isSelected = dateStr === selectedDate;
                                    const dayTasks = schedulesByDate[dateStr] || [];
                                    const hasTasks = dayTasks.length > 0;
                                    return (
                                        <button
                                            key={dateStr}
                                            className={`cal-day${isOtherMonth ? ' cal-day--other' : ''}${isToday ? ' cal-day--today' : ''}${isSelected ? ' cal-day--selected' : ''}${hasTasks ? ' cal-day--has-tasks' : ''}`}
                                            onClick={() => handleDayClick(dateStr)}
                                        >
                                            <span className={`cal-day__number${isToday ? ' cal-day__number--today' : ''}`}>
                                                {day}
                                            </span>
                                            {hasTasks && (
                                                <div className="cal-day__events">
                                                    {dayTasks.slice(0, 5).map((task, i) => (
                                                        <div key={task.id + '-' + i} className="cal-event-pill"
                                                            style={{ backgroundColor: getTaskColor(task._colorIdx) }}>
                                                            <span className="cal-event-pill__text">
                                                                {task.time ? `${task.time} ` : ''}
                                                                {(() => {
                                                                    const maxLen = task.time ? 7 : 10;
                                                                    return task.title.length > maxLen
                                                                        ? task.title.slice(0, maxLen) + '..'
                                                                        : task.title;
                                                                })()}
                                                            </span>
                                                        </div>
                                                    ))}
                                                    {dayTasks.length > 5 && (
                                                        <div className="cal-event-more">+{dayTasks.length - 5}</div>
                                                    )}
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* ===== DAY VIEW ===== */}
                    {viewMode === 'day' && (
                        <div className="cal-day-view">
                            <div className="cal-detail__header">
                                <button className="cal-detail__back" onClick={goBackToMonth}>← Буцах</button>
                                <h3 className="cal-detail__dayname">{formatSelectedDate(selectedDate)}</h3>
                            </div>

                            {untimedTasks.length > 0 && (
                                <div className="cal-untimed">
                                    <div className="cal-untimed__label">Цаггүй</div>
                                    {untimedTasks.map(task => (
                                        <div key={task.id} className="cal-untimed__item"
                                            style={{ borderLeftColor: getTaskColor(task._colorIdx) }}
                                            onClick={() => navigate(`/edit/${task.id}`)}>
                                            <div className="cal-untimed__title">{task.title}</div>
                                            {task.destination && <div className="cal-untimed__detail">📍 {task.destination}</div>}
                                            {task.meetupPerson && <div className="cal-untimed__detail">👤 {task.meetupPerson}</div>}
                                            {task.imageUrl && (
                                                <div className="cal-task-block__img-wrap">
                                                    <img src={task.imageUrl} alt="" className="cal-task-block__img"
                                                        onClick={(e) => { e.stopPropagation(); setExpandedImage(task.imageUrl); }} />
                                                </div>
                                            )}
                                            {task.voiceUrl && (
                                                <div className="cal-task-block__audio-wrap" onClick={e => e.stopPropagation()}>
                                                    <audio controls src={task.voiceUrl} className="cal-task-block__audio" />
                                                </div>
                                            )}
                                            <div className="cal-task-block__actions" onClick={e => e.stopPropagation()}>
                                                <button className="cal-task-btn" onClick={() => navigate(`/edit/${task.id}`)}>✏️ Засах</button>
                                                <button className="cal-task-btn cal-task-btn--del" onClick={() => handleDelete(task.id)}>🗑️ Устгах</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="cal-timeline">
                                {timelineHours.map(hour => {
                                    const hourTasks = timedTasks.filter(t => Math.floor(t.startHour) === hour);
                                    return (
                                        <div key={hour} className="cal-hour">
                                            <span className="cal-hour__label">{String(hour).padStart(2, '0')}:00</span>
                                            <div className="cal-hour__line"></div>
                                            <div className="cal-hour__content">
                                                {hourTasks.map(task => (
                                                    <div key={task.id} className="cal-task-block"
                                                        style={{
                                                            backgroundColor: getTaskColor(task._colorIdx),
                                                            top: `${(task.startHour - hour) * 100}%`,
                                                        }}
                                                        onClick={() => navigate(`/edit/${task.id}`)}>
                                                        <div className="cal-task-block__title">{task.title}</div>
                                                        {task.destination && <div className="cal-task-block__place">📍 {task.destination}</div>}
                                                        <div className="cal-task-block__time">🕐 {task.time}</div>
                                                        {task.meetupPerson && <div className="cal-task-block__person">👤 {task.meetupPerson}</div>}
                                                        {task.imageUrl && (
                                                            <div className="cal-task-block__img-wrap">
                                                                <img src={task.imageUrl} alt="" className="cal-task-block__img"
                                                                    onClick={(e) => { e.stopPropagation(); setExpandedImage(task.imageUrl); }} />
                                                            </div>
                                                        )}
                                                        {task.voiceUrl && (
                                                            <div className="cal-task-block__audio-wrap" onClick={e => e.stopPropagation()}>
                                                                <audio controls src={task.voiceUrl} className="cal-task-block__audio" />
                                                            </div>
                                                        )}
                                                        <div className="cal-task-block__actions" onClick={e => e.stopPropagation()}>
                                                            <button className="cal-task-btn" onClick={() => navigate(`/edit/${task.id}`)}>✏️ Засах</button>
                                                            <button className="cal-task-btn cal-task-btn--del" onClick={() => handleDelete(task.id)}>🗑️ Устгах</button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {selectedDayTasks.length === 0 && (
                                <div className="cal-empty">
                                    <div className="cal-empty__icon">📭</div>
                                    <p>Энэ өдөр ажил хуваарь алга</p>
                                    <Link to={`/create?date=${selectedDate}`} className="btn btn-primary">+ Хуваарь нэмэх</Link>
                                </div>
                            )}
                        </div>
                    )}


                </>
            )}

            {expandedImage && (
                <div className="lightbox" onClick={() => setExpandedImage(null)}>
                    <img src={expandedImage} alt="Зураг" className="lightbox__img" />
                    <button className="lightbox__close" onClick={() => setExpandedImage(null)}>✕</button>
                </div>
            )}
        </div>
    );
}

export default Dashboard;
